import React, { createContext, useContext, useEffect, useMemo } from "react";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";

const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
const RECAPTCHA_ENABLED = process.env.REACT_APP_RECAPTCHA_ENABLED === "true";

/** Toggled by <RecaptchaGate> so the badge shows only on protected forms. */
export const RECAPTCHA_BADGE_VISIBLE_CLASS = "recaptcha-badge-visible";

const noopExecuteRecaptcha = () => Promise.resolve(null);

const RecaptchaContext = createContext({
  executeRecaptcha: noopExecuteRecaptcha,
  // True once a caller-triggered executeRecaptcha() will actually round-trip
  // to Google rather than resolve to a token-less no-op. Pages that auto-fire
  // on mount (e.g. Onboarding's verify-email autologin) need this to avoid
  // sending a request the BE will 400 for lacking a token; button-triggered
  // flows (Login/Signup submit) don't check it because the script has almost
  // always finished loading by the time a human fills out the form.
  isReady: true,
});

// Stable object identities — the library re-inits the script when these change.
const SCRIPT_PROPS = { async: true, defer: true, appendTo: "head" };
const CONTAINER = {
  parameters: {
    badge: "bottomright",
    theme: "dark",
  },
};

/**
 * Inner component that runs inside GoogleReCaptchaProvider and bridges
 * the library's hook to our context (only used when RECAPTCHA_ENABLED is true).
 */
function RecaptchaContextBridge({ children }) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const value = useMemo(
    () => ({
      executeRecaptcha: executeRecaptcha || noopExecuteRecaptcha,
      // useGoogleReCaptcha() itself is the true not-ready signal (undefined
      // until the script + widget finish loading) -- capture that BEFORE the
      // noop substitution above, which exists so callers never need a null
      // check and would otherwise mask "not ready" as an always-truthy fn.
      isReady: !!executeRecaptcha,
    }),
    [executeRecaptcha]
  );
  return (
    <RecaptchaContext.Provider value={value}>
      {children}
    </RecaptchaContext.Provider>
  );
}

/**
 * App-root provider: loads Google reCAPTCHA v3 once for the whole app.
 * Mounting/unmounting the provider per-page causes "reCAPTCHA Timeout (b)".
 *
 * The floating badge is hidden by CSS on public pages; wrap protected forms in
 * `<RecaptchaGate>` to reveal it while keeping executeRecaptcha available.
 */
export function ConditionalRecaptchaProvider({ children }) {
  if (RECAPTCHA_ENABLED && RECAPTCHA_SITE_KEY) {
    return (
      <GoogleReCaptchaProvider
        reCaptchaKey={RECAPTCHA_SITE_KEY}
        scriptProps={SCRIPT_PROPS}
        container={CONTAINER}
      >
        <RecaptchaContextBridge>{children}</RecaptchaContextBridge>
      </GoogleReCaptchaProvider>
    );
  }

  return (
    <RecaptchaContext.Provider
      value={{ executeRecaptcha: noopExecuteRecaptcha, isReady: true }}
    >
      {children}
    </RecaptchaContext.Provider>
  );
}

// Refcount so nested gates / Strict Mode remounts don't flicker the badge.
let badgeVisibleCount = 0;

function syncBadgeVisibleClass() {
  if (typeof document === "undefined") return;
  document.body.classList.toggle(
    RECAPTCHA_BADGE_VISIBLE_CLASS,
    badgeVisibleCount > 0
  );
}

/**
 * Reveal the reCAPTCHA badge while this subtree is mounted (Login/Signup/
 * ForgotPassword, Settings Password, Contact Support). Does NOT mount another
 * Google provider — that must stay singleton at the app root.
 *
 * `enabled={false}` keeps the subtree mounted but leaves the badge hidden
 * (the caller must then show Google's inline attribution text instead).
 */
export function RecaptchaGate({ children, enabled = true }) {
  useEffect(() => {
    if (!enabled || !(RECAPTCHA_ENABLED && RECAPTCHA_SITE_KEY)) return undefined;
    badgeVisibleCount += 1;
    syncBadgeVisibleClass();
    return () => {
      badgeVisibleCount = Math.max(0, badgeVisibleCount - 1);
      syncBadgeVisibleClass();
    };
  }, [enabled]);

  return children;
}

export function useRecaptcha() {
  return useContext(RecaptchaContext);
}
